import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { ArrowLeft, Scale, Users, Award, CheckCircle } from 'lucide-react';

const StatisticsScreen = () => {
  const navigate = useNavigate();
  const { language } = useApp();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const translations = {
    english: {
      title: 'Our Impact',
      subtitle: 'Trusted by thousands of users for legal assistance',
      verifiedLawyers: 'Verified Lawyers',
      legalCategories: 'Legal Categories',
      successRate: 'Success Rate',
      getStarted: 'Get Started',
      termsText: 'I agree to the terms and conditions and privacy policy',
      back: 'Back'
    },
    hindi: {
      title: 'हमारा प्रभाव',
      subtitle: 'कानूनी सहायता के लिए हजारों उपयोगकर्ताओं द्वारा विश्वसनीय',
      verifiedLawyers: 'सत्यापित वकील',
      legalCategories: 'कानूनी श्रेणियां',
      successRate: 'सफलता दर',
      getStarted: 'शुरू करें',
      termsText: 'मैं नियम और शर्तों और गोपनीयता नीति से सहमत हूं',
      back: 'वापस'
    }
  };

  const t = translations[language] || translations.english;

  const statistics = [
    {
      number: '15,000+',
      label: t.verifiedLawyers,
      icon: <Users className="w-6 h-6 text-black-600" />
    },
    {
      number: '50+',
      label: t.legalCategories,
      icon: <Award className="w-6 h-6 text-black-600" />
    },
    {
      number: '95%',
      label: t.successRate,
      icon: <CheckCircle className="w-6 h-6 text-black-600" />
    }
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const handleGetStarted = () => {
    if (agreedToTerms) {
      navigate('/auth');
    } else {
      alert('Please agree to the terms and conditions to continue.');
    }
  };

  return (
    <div className="min-h-screen bg-white max-w-md mx-auto relative">
      {/* Header Section */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t.title}</h1>
            <p className="text-xs text-gray-500">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-8">
        {/* Justice Scale Icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-6"
        >
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
            <Scale className="w-8 h-8 text-white" />
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-3 mb-52"
        >
          {statistics.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="bg-gray-50 rounded-lg p-3 border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm">
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 font-medium">{stat.label}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-purple-600">{stat.number}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Terms and Conditions - Inside Page Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mb-4"
        >
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-300 focus:ring-2"
            />
            <span className="text-xs text-gray-700 leading-relaxed">
              {t.termsText}
            </span>
          </label>
        </motion.div>

        {/* Get Started Button - Inside Page Content */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          onClick={handleGetStarted}
          disabled={!agreedToTerms}
          className={`w-full h-12 rounded-lg font-medium text-sm transition-all duration-200 ${
            agreedToTerms
              ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {t.getStarted}
        </motion.button>
      </div>
    </div>
  );
};

export default StatisticsScreen;
